import React, {useState, useMemo, ReactElement} from 'react';
import {CARET_DOWN__16} from '@homeaway/svg-defs';
import {useIntl} from 'react-intl';

import FilterPill from '../FilterPill/FilterPill';
import FilterGroupModal from '../FilterGroupModal/FilterGroupModal';
import FilterGroupDropdown from '../FilterGroupDropdown/FilterGroupDropdown';
import FilterGroupContent from '../FilterGroupContent/FilterGroupContent';
import {
    getFilterGroupPillLabel,
    getAppliedCount,
} from '../../utils/filterPillUtils';

import {Props as FilterGroupFiltersProps} from '../FilterGroupFilters/FilterGroupFilters';
import {Props as FilterBarProps} from '../FilterBarV2/FilterBarV2';

type ExtendedProp = Pick<
    FilterBarProps,
    | 'appliedFilters'
    | 'currency'
    | 'customFiltersParams'
    | 'isMobile'
    | 'onClear'
    | 'onDismiss'
    | 'onDone'
    | 'onFilterToggle'
    | 'onMultipleFilterToggle'
    | 'priceFilterOptions'
    | 'priceInputValuesAreValid'
    | 'results'
    | 'selectedFilters'
    | 'onCustomFilterUpdate'
    | 'updatePriceInputValuesValidity'
>;
export interface Props extends ExtendedProp, FilterGroupFiltersProps {
    label: string;
    secondaryLabel?: string;
    scrolling: boolean;
}

export default function FilterGroupPill(props: Props) {
    const {
        groupId,
        label,
        isMobile,
        filters,
        appliedFilters,
        customFiltersParams,
        currency,
        priceFilterOptions = {},
    } = props;
    const [isGroupOpen, setIsGroupOpen] = useState(false);
    const {formatMessage, locale} = useIntl();

    const filterPillLabel = useMemo<string>(
        () =>
            getFilterGroupPillLabel({
                appliedFilters,
                filters,
                groupId,
                label,
                locale,
                formatMessage,
                currency,
                customFiltersParams,
                priceFilterOptions,
            }),
        [
            appliedFilters,
            filters,
            groupId,
            label,
            locale,
            formatMessage,
            currency,
            customFiltersParams,
            priceFilterOptions,
        ]
    );

    const appliedCount = useMemo<number>(
        () =>
            getAppliedCount({
                appliedFilters,
                filters,
                groupId,
                customFiltersParams,
            }),
        [appliedFilters, filters, groupId, customFiltersParams]
    );

    const filterPill = useMemo<ReactElement>(() => {
        return (
            <FilterPill
                edapLabel={groupId}
                label={filterPillLabel}
                icon={CARET_DOWN__16}
                rotateIcon={isGroupOpen}
                focused={isGroupOpen}
                appliedCount={appliedCount}
                applied={appliedCount > 0 || filterPillLabel !== label}
            />
        );
    }, [filterPillLabel, label, isGroupOpen, appliedCount, groupId]);

    const ComponentType = isMobile ? FilterGroupModal : FilterGroupDropdown;

    return (
        <ComponentType
            {...props}
            filterPill={filterPill}
            setIsGroupOpen={setIsGroupOpen}
        >
            <FilterGroupContent {...props} isGroupOpen={isGroupOpen} />
        </ComponentType>
    );
}
